/**
 * The backup password on screen (decision 72; part 41b log, principles BP1, BP7, BP8, table E; `cockatrice-ui`
 * `BackupSync.jsx`'s "Backup password" row and `SyncSetup.jsx`'s recovery-key check, the user's choice 2026-10-30): the
 * row in Settings › Backups, its sheets (set or change, with the strength rule and the tick that a forgotten password
 * can't be recovered; turn off, confirmed (3.3.6); the check), and the check's notice under the toolbar, always put
 * off-able (2.2.3, 2.2.4). The password goes to the backups' controller and nowhere else (BP2).
 */
import { ModalBody, ModalButtons } from './modal.ts';
import { createSignal, untrack } from 'solid-js';
import { ErrorText, focusField } from './account-fields.ts';
import { focusHeading, Illustration, SheetButton, SheetHead, SheetNewPassword, SheetPassword, SheetSubmit } from './account-sheet.ts';
import { focusOn } from './announce.ts';
import { el, when } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import { focusHome } from './home.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import UiIcon from '../ui/Icon.ts';
import SettingsRow from '../ui/SettingsRow.ts';
import { component } from '../ui/solid.ts';
import type { Shell } from './shell.ts';

/** What a backup password's sheet does: set one (BP1), change it (E6, BP8), turn it off (E5), or check it (E15–E17). */
export type LockTask = 'set' | 'change' | 'off' | 'check';

/** A sheet's step: its form, or what was done. */
type LockStep = { readonly kind: 'form' } | { readonly kind: 'done'; readonly words: string };

/** A button that opens the sheet for `task`. */
function opener(shell: Shell, task: LockTask, label: string): Child {
  return component(Button, { size: 'sm', class: `lock-${task}`, onClick: () => shell.ui.setDialog({ kind: 'backup-lock', task }), children: label });
}

/**
 * E1, E2, E7: the "Backup password" row of Settings › Backups: off, the design's help and Set password; on, that the
 * backups are locked with it (checked here, never sent, can't be opened without it) and Check, Change, Turn off;
 * unreadable, that backups are paused until it is set again, and Set password.
 */
export function LockRow(shell: Shell): Child {
  const { t, backups } = shell;
  // PLAN.md §3, step 1: a SettingsRow; its label is `backup-lock-label`, which the buttons' group is named by.
  return component(SettingsRow, {
    class: 'backup-lock',
    ids: 'backup-lock',
    label: t('lock.label'),
    get help() {
      switch (backups.lock()) {
        case 'off':
          return t('lock.help');
        case 'on':
          return t('lock.helpOn');
        case 'unreadable':
          return el('span', { class: 'backup-lock-warn' }, el('span', { class: 'warn-mark' }, component(UiIcon, { name: 'warn', size: 'sm' })), el('span', {}, t('lock.unreadable')));
      }
    },
    get children() {
      // The buttons are named in the row's group, so "Check" reads as the backup password's.
      return el('div', { class: 'backup-lock-actions', role: 'group', 'aria-labelledby': 'backup-lock-label' }, () =>
        backups.lock() === 'on' ? [opener(shell, 'check', t('lock.check')), opener(shell, 'change', t('lock.change')), opener(shell, 'off', t('lock.offOpen'))] : opener(shell, 'set', t('lock.set')),
      );
    },
  });
}

/** O23: the backups' disclosure line, true in each state (E9: a bundle exported by hand stays plain; E7: paused, not locked). */
export function plainText(shell: Shell): string {
  const lock = shell.backups.lock();
  if (lock === 'unreadable') return shell.t('backup.lockPaused');
  return lock === 'off' ? shell.t('backup.plain') : shell.t('backup.locked');
}

/** E15–E17: the check's notice under the toolbar while one is due: Check opens its sheet; Remind me later puts it off. */
export function LockCheckNotice(shell: Shell): Child {
  const { t, backups, ui } = shell;
  return when(
    () => backups.lockCheckDue(),
    () =>
      el(
        'section',
        { class: 'export-status backup-lock-check', 'aria-label': t('lock.noticeLabel') },
        el('p', {}, t('lock.notice')),
        el('button', { type: 'button', onClick: () => ui.setDialog({ kind: 'backup-lock', task: 'check' }) }, t('lock.check')),
        el(
          'button',
          {
            type: 'button',
            onClick: () => {
              backups.checkLockLater();
              // A24: the region goes with the press, so the focus goes back to the text.
              shell.focusEditor();
            },
          },
          t('lock.later'),
        ),
      ),
  );
}

/**
 * The focus after a backup password's sheet closes: where it was, unless the button that opened it went meanwhile (the
 * row's buttons follow the lock, the check's notice goes once checked); then the row's first button, or the page's start.
 */
export function refocusLock(shell: Shell): void {
  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body && active.isConnected) return;
  const route = shell.ui.route();
  if (route === 'settings') focusOn(shell, '.backup-lock button');
  else if (route === 'write') shell.focusEditor();
  else focusHome();
}

/** The sheet's heading for `task`. */
function title(shell: Shell, task: LockTask): string {
  const { t } = shell;
  switch (task) {
    case 'set':
      return t('lock.setTitle');
    case 'change':
      return t('lock.changeTitle');
    case 'off':
      return t('lock.offTitle');
    case 'check':
      return t('lock.checkTitle');
  }
}

/** A backup password's sheet for `task`: its form, then what was done; a failure is said on the form (BP3: never silent). */
export function LockSheet(shell: Shell, task: LockTask): Child {
  const { t, backups, ui } = shell;
  const [step, setStep] = createSignal<LockStep>({ kind: 'form' });
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal('');
  const done = (words: string): void => {
    setError('');
    setStep({ kind: 'done', words });
    focusHeading();
  };
  const cancel = (): Child => SheetButton(t('account.sheet.cancel'), () => ui.setDialog(null), 'sheet-cancel');
  const form = (): Child => {
    switch (task) {
      case 'set':
      case 'change':
        return NewForm(shell, task, busy, setBusy, setError, done, cancel());
      case 'off':
        return el(
          'div',
          { class: 'c-modal__form lock-off-form' },
          ModalBody({}, el('p', { class: 'ac-lead' }, t('lock.offLead'))),
          ModalButtons(
            cancel(),
            component(Button, {
              variant: 'danger',
              class: 'lock-off-confirm',
              get 'aria-busy'() {
                return busy() ? 'true' : 'false';
              },
              'aria-describedby': 'sheet-error',
              onClick: () => {
                if (busy()) return;
                setBusy(true);
                setError('');
                void backups.turnOffLock().then((outcome) => {
                  setBusy(false);
                  if (outcome === 'off') done(t('lock.offDone'));
                  else setError(t('lock.error.off'));
                });
              },
              children: t('lock.off'),
            }),
          ),
        );
      case 'check':
        return CheckForm(shell, busy, setBusy, setError, done);
    }
  };
  // The part log's accessibility list: a form's first field takes the focus once the sheet is open; Turn off's
  // confirmation has none, so its heading does, its words read first.
  if (task === 'off') focusHeading();
  else
    queueMicrotask(() => {
      focusField('lock-pw');
    });
  return [
    SheetHead(shell, () => t('lock.sheet'), () => title(shell, task)),
    () => {
      const now = step();
      // Made untracked, as a component is, so nothing it reads at its making makes it again.
      return now.kind === 'form'
        ? untrack(form)
        : el(
            'div',
            { class: 'c-modal__form outcome' },
            ModalBody({}, Illustration('check', true), el('p', { class: 'ac-lead', role: 'status' }, now.words)),
            ModalButtons(SheetButton(t('account.sheet.done'), () => ui.setDialog(null), 'sheet-done', 'primary')),
          );
    },
    el('div', { class: 'c-modal__error' }, ErrorText('sheet-error', error)),
  ];
}

/**
 * Set or Change (BP1, BP8, E6): the password with the strength rule (decision 25's, as the account's), typed again, and
 * the tick that a forgotten one can't be recovered; Change says older backups keep the old one.
 */
function NewForm(shell: Shell, task: 'set' | 'change', busy: () => boolean, setBusy: (busy: boolean) => void, setError: (text: string) => void, done: (words: string) => void, cancel: Child): Child {
  const { t, backups } = shell;
  const [password, setPassword] = createSignal('');
  const [repeat, setRepeat] = createSignal('');
  const [understood, setUnderstood] = createSignal(false);
  const strong = SheetNewPassword(shell, 'lock-pw', () => '', setPassword, t('lock.field'));
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    // Busy while the strength check waits for its scorer, so a second press meanwhile sets nothing.
    setBusy(true);
    const weak = await strong.check();
    setBusy(false);
    if (weak !== null) {
      setError(weak);
      focusField('lock-pw');
      return;
    }
    if (repeat() !== password()) {
      setError(t('lock.error.repeat'));
      focusField('lock-repeat');
      return;
    }
    if (!understood()) {
      setError(t('lock.error.understood'));
      focusField('lock-understood');
      return;
    }
    setBusy(true);
    setError('');
    const outcome = await backups.setLock(password());
    setBusy(false);
    if (outcome === 'set') done(t(task === 'set' ? 'lock.setDone' : 'lock.changeDone'));
    else setError(t('lock.error.set'));
  };
  return el(
    'form',
    { class: 'c-modal__form lock-new', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
    ModalBody({},
      el('p', { class: 'ac-lead' }, t(task === 'set' ? 'lock.setLead' : 'lock.changeLead')),
      strong.field,
      SheetPassword(shell, 'lock-repeat', 'new-password', setRepeat, t('lock.repeat')),
      component(Checkbox, {
        id: 'lock-understood',
        label: t('lock.understood'),
        required: true,
        get checked() {
          return understood();
        },
        'aria-describedby': 'sheet-error',
        onChange: (checked: boolean) => {
          setUnderstood(checked);
        },
      }),
    ),
    ModalButtons(cancel, SheetSubmit(t(task === 'set' ? 'lock.setSubmit' : 'lock.changeSubmit'), busy, t('lock.setting'))),
  );
}

/**
 * E15–E17 (BP7): the check: the password, checked on this device; right, said; wrong, "That isn't it", tried again, with
 * the ways on (set a new one, older backups keeping the old; or turn it off); Remind me later puts it off a day.
 */
function CheckForm(shell: Shell, busy: () => boolean, setBusy: (busy: boolean) => void, setError: (text: string) => void, done: (words: string) => void): Child {
  const { t, backups, ui } = shell;
  const [password, setPassword] = createSignal('');
  const [wrong, setWrong] = createSignal(false);
  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (busy()) return;
    if (password() === '') {
      setError(t('account.error.password'));
      focusField('lock-pw');
      return;
    }
    setBusy(true);
    setError('');
    const outcome = await backups.checkLock(password());
    setBusy(false);
    if (outcome === 'right') {
      done(t('lock.right'));
      return;
    }
    if (outcome === 'wrong') setWrong(true);
    setError(t(outcome === 'wrong' ? 'lock.wrong' : 'lock.error.check'));
    focusField('lock-pw');
  };
  return el(
    'form',
    { class: 'c-modal__form lock-check-form', noValidate: true, onSubmit: (event: SubmitEvent) => void submit(event) },
    ModalBody({},
      el('p', { class: 'ac-lead' }, t('lock.checkLead')),
      SheetPassword(shell, 'lock-pw', 'current-password', setPassword, t('lock.field')),
      el('p', { class: 'ac-hint lock-hint' }, component(UiIcon, { name: 'lock', size: 'sm', class: 'lock-hint__icon' }), ' ', t('lock.checkHint')),
      // E16, design round 2: the ways on, after a wrong try or when the writer says they can't find it.
      when(wrong, () =>
        el(
          'div',
          { class: 'lock-ways' },
          el('p', { class: 'ac-hint' }, t('lock.wrongWays')),
          el(
            'div',
            { class: 'ac-actions' },
            opener(shell, 'change', t('lock.newPassword')),
            opener(shell, 'off', t('lock.offOpen')),
          ),
        ),
      ),
    ),
    ModalButtons(
      SheetButton(
        t('lock.lost'),
        () => {
          setWrong(true);
          setError('');
          // The focus lands where the next step is: the first way on.
          document.querySelector<HTMLElement>('.lock-ways button')?.focus();
        },
        'lock-lost',
      ),
      SheetButton(
        t('lock.later'),
        () => {
          backups.checkLockLater();
          ui.setDialog(null);
        },
        'lock-later',
      ),
      SheetSubmit(t('lock.check'), busy, t('account.working')),
    ),
  );
}
